// ── Modal styles ───────────────────────────────────────────────────────────────
// Edit this file to change the visual appearance of the entire SDK UI.
// Tokens (colours) are imported from tokens.ts.

import { TEXT, MUTED, BORDER, ERR, BG, FONT } from './tokens';

export const STYLE_ID = '__ts-modal-style__';

export function injectStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
:root{--ts-primary-light:#7FD4E8;--ts-primary-dark:#1F6F8F;--ts-btn-radius:12px;--ts-btn-padding:11px 16px;--ts-btn-font:15px;--ts-modal-primary:#7FD4E8;--ts-modal-radius:var(--ts-btn-radius);--ts-modal-pad:10px;--ts-gap:10px}
/* Themed semantic neutrals — light defaults. Only these flip with the theme;
   brand/structural configurator vars above always stay merchant-set. */
:root{
  --ts-text:#0F172A;--ts-body:#334155;--ts-muted:#64748B;--ts-border:#E2E8F0;--ts-bg:#F8FAFC;--ts-error:#E11D48;
  --ts-card-bg:rgba(255,255,255,.97);--ts-surface:#fcfdfe;--ts-dropzone-border:#C9D5D9;
  --ts-placeholder:#9aa3ab;--ts-error-bg:#FFF1F2;--ts-error-border:#FECDD3;--ts-result-bg:#f5f5f5;
  --ts-tint:#F1FBFE;
  --ts-powered:#64748B;
}
/* colorScheme:'auto' — follow the OS. */
@media (prefers-color-scheme: dark){
  :root{
    --ts-text:#F1F5F9;--ts-body:#CBD5E1;--ts-muted:#94A3B8;--ts-border:#334155;--ts-bg:#1E293B;--ts-error:#FB7185;
    --ts-card-bg:rgba(15,23,42,.97);--ts-surface:#111827;--ts-dropzone-border:#334155;
    --ts-placeholder:#64748B;--ts-error-bg:#2A1218;--ts-error-border:#701A24;--ts-result-bg:#0B0F1A;
    --ts-tint:#0D2731;
  }
  .ts-overlay{color-scheme:dark}
}
/* colorScheme:'dark'|'light' — forced via data-ts-theme on the modal overlay
   (element-scoped, so it wins over the :root media rule on this subtree). */
.ts-overlay[data-ts-theme="dark"]{
  --ts-text:#F1F5F9;--ts-body:#CBD5E1;--ts-muted:#94A3B8;--ts-border:#334155;--ts-bg:#1E293B;--ts-error:#FB7185;
  --ts-card-bg:rgba(15,23,42,.97);--ts-surface:#111827;--ts-dropzone-border:#334155;
  --ts-placeholder:#64748B;--ts-error-bg:#2A1218;--ts-error-border:#701A24;--ts-result-bg:#0B0F1A;
  --ts-tint:#0D2731;
  color-scheme:dark;
}
.ts-overlay[data-ts-theme="light"]{
  --ts-text:#0F172A;--ts-body:#334155;--ts-muted:#64748B;--ts-border:#E2E8F0;--ts-bg:#F8FAFC;--ts-error:#E11D48;
  --ts-card-bg:rgba(255,255,255,.97);--ts-surface:#fcfdfe;--ts-dropzone-border:#C9D5D9;
  --ts-placeholder:#9aa3ab;--ts-error-bg:#FFF1F2;--ts-error-border:#FECDD3;--ts-result-bg:#f5f5f5;
  --ts-tint:#F1FBFE;
  color-scheme:light;
}
/* Shield SDK buttons from host page margin leakage (WordPress etc.) */
button.ts-tryon-btn, button.ts-btn-primary, button.ts-btn-ghost, button.ts-btn-secondary, button.ts-close, button.ts-login-link, .ts-video-toolbar button, button.ts-avatar-upload-btn, button.ts-result-action, button.ts-video-close, button.ts-mv-pill, button.ts-mv-arrow, button.ts-logout-btn, button.ts-social-btn {
  margin-left: 0 !important;
}
/* Overlay */
.ts-overlay{position:fixed;inset:0;z-index:99998;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(15,23,42,.5);font-family:${FONT}}
.ts-overlay, .ts-overlay *{box-sizing:border-box}
.ts-overlay button, .ts-overlay input, .ts-overlay textarea, .ts-overlay select{font:inherit;color:inherit;background:transparent;border:0;outline:none;margin:0;appearance:none;-webkit-appearance:none;-moz-appearance:none}
.ts-overlay button{cursor:pointer}
.ts-overlay .ts-btn-primary, .ts-overlay .ts-tryon-btn, .ts-overlay .ts-btn-ghost, .ts-overlay .ts-logout-btn, .ts-overlay .ts-result-action, .ts-hoverlay .ts-btn-primary{background:initial;border:initial;color:inherit}
/* Main card */
.ts-card{background:var(--ts-card-bg);border-radius:var(--ts-btn-radius);width:100%;max-width:440px;max-height:92vh;overflow-y:auto;box-shadow:0 1px 2px rgba(15,23,42,.05),0 24px 64px -12px rgba(15,23,42,.24);scrollbar-width:none;-ms-overflow-style:none;position:relative;color:${TEXT}}
.ts-overlay,.ts-hoverlay{animation:ts-backdrop-in .4s ease}
@keyframes ts-backdrop-in{from{opacity:0}to{opacity:1}}
@media (prefers-reduced-motion: reduce){.ts-overlay,.ts-hoverlay,.ts-combo-carousel{animation:none}}
.ts-card::-webkit-scrollbar{display:none}
/* Close (used by login + height dialogs) */
.ts-close{position:absolute;top:14px;right:16px;width:30px;height:30px;display:flex;align-items:center;justify-content:center;background:var(--ts-card-bg) !important;border:none;border-radius:50%;font-size:16px;cursor:pointer;color:${MUTED} !important;line-height:1;z-index:2;padding:0;transition:background .15s,color .15s}
.ts-close:hover{background:${BG} !important;color:${TEXT} !important}
/* Logo */
.ts-logo{position:relative;display:inline-flex;align-items:center}
.ts-logo-tl,.ts-logo-br{position:absolute}
.ts-logo-tl{top:0;left:0;border-top:1.8px solid var(--ts-primary-light);border-left:1.8px solid var(--ts-primary-light)}
.ts-logo-br{bottom:0;right:0;border-bottom:1.8px solid var(--ts-primary-light);border-right:1.8px solid var(--ts-primary-light)}
.ts-logo-try{font-weight:700;color:var(--ts-primary-light);line-height:1.2;letter-spacing:-.3px}
.ts-logo-style{font-weight:700;color:${TEXT};line-height:1.2;letter-spacing:-.3px}
/* Header */
.ts-mhdr{padding:var(--ts-modal-pad) var(--ts-modal-pad) 0;text-align:center;position:relative}
.ts-mhdr-tagline{font-size:12px;color:${MUTED};margin:6px 0 0;line-height:1.5;letter-spacing:.01em}
.ts-divider{height:1px;background:${BORDER};margin:var(--ts-gap) 0 0}
/* Body */
.ts-mbody{padding:var(--ts-modal-pad)}
.ts-flow-layout{display:block}
.ts-flow-panel{min-width:0}
.ts-flow-panel{min-width:0;position:relative;padding-bottom:0}
/* Topbar — cohesive, flush logout row (in-flow, right-aligned, no clearance hack) */
.ts-topbar{display:flex;align-items:center;justify-content:flex-end;margin-bottom:var(--ts-gap)}
.ts-logout-btn{width:32px;height:32px;display:flex;align-items:center;justify-content:center;padding:0;background:var(--ts-card-bg);border:1px solid ${BORDER};border-radius:50%;color:var(--ts-modal-primary);cursor:pointer;box-shadow:0 1px 2px rgba(15,23,42,.05);transition:color .15s,border-color .15s,background .15s;flex-shrink:0}
.ts-logout-btn .ts-icon-svg{width:18px;height:18px;display:block;fill:none}
.ts-logout-btn:hover{color:var(--ts-primary-dark);border-color:var(--ts-primary-dark);background:rgba(127,212,232,.12)}
/* Global trigger button */
.ts-tryon-btn-wrap{position:relative;display:inline-flex;border-radius:var(--ts-btn-radius);vertical-align:middle}
.ts-tryon-btn{position:relative;display:inline-flex;white-space:nowrap;align-items:center;justify-content:center;padding:var(--ts-btn-padding);font-weight:600;font-size:var(--ts-btn-font);text-align:center;border-radius:var(--ts-btn-radius);color:#fff !important;border:none;cursor:pointer;box-sizing:border-box;letter-spacing:.01em;font-family:${FONT};background:linear-gradient(135deg,var(--ts-primary-light),var(--ts-primary-dark));box-shadow:0 4px 14px rgba(31,111,143,.28);transition:transform .15s ease,box-shadow .15s ease,opacity .15s ease}
.ts-tryon-btn:hover{transform:translateY(-1px);box-shadow:0 6px 18px rgba(31,111,143,.34)}
.ts-tryon-btn.ts-disabled,.ts-tryon-btn-wrap.ts-disabled .ts-tryon-btn{opacity:.45;cursor:not-allowed;background:#a0a0a0 !important;box-shadow:none !important;transform:none;pointer-events:none;filter:grayscale(.8)} /* paired with initFailed guard in click handler */
.ts-tryon-btn.ts-disabled:hover,.ts-tryon-btn-wrap.ts-disabled .ts-tryon-btn:hover{opacity:.45;transform:none;box-shadow:none !important}
/* Shimmer sweep on the trigger button (opt-in via config.shimmer) */
.ts-tryon-btn.ts-shimmer{overflow:hidden}
.ts-tryon-btn.ts-shimmer::after{content:'';position:absolute;inset:0;pointer-events:none;background-image:linear-gradient(100deg,transparent 35%,rgba(255,255,255,.55) 50%,transparent 65%);background-size:220% 100%;background-repeat:no-repeat;background-position:200% 0;animation:ts-shimmer-lr var(--ts-shimmer-duration,2.6s) linear infinite}
.ts-tryon-btn.ts-shimmer-rl::after{animation-name:ts-shimmer-rl}
.ts-tryon-btn.ts-shimmer-diag::after{background-size:250% 250%;background-image:linear-gradient(120deg,transparent 40%,rgba(255,255,255,.5) 50%,transparent 60%);animation-name:ts-shimmer-diag-lr}
.ts-tryon-btn.ts-shimmer-diag.ts-shimmer-rl::after{animation-name:ts-shimmer-diag-rl}
@keyframes ts-shimmer-lr{0%{background-position:200% 0}100%{background-position:-200% 0}}
@keyframes ts-shimmer-rl{0%{background-position:-200% 0}100%{background-position:200% 0}}
@keyframes ts-shimmer-diag-lr{0%{background-position:200% 200%}100%{background-position:-200% -200%}}
@keyframes ts-shimmer-diag-rl{0%{background-position:-200% -200%}100%{background-position:200% 200%}}
.ts-tryon-btn.ts-shimmer.ts-disabled::after,.ts-tryon-btn-wrap.ts-disabled .ts-tryon-btn::after{display:none}
@media (prefers-reduced-motion: reduce){.ts-tryon-btn.ts-shimmer::after{animation:none;display:none}}
/* Upload heading */
.ts-upload-heading{display:flex;flex-wrap:wrap;justify-content:center;align-items:center;gap:4px;margin:0;height:34px;font-size:12px;line-height:1.5;color:${MUTED}}
.ts-upload-heading strong{font-weight:600;font-size:12px;color:${TEXT};}
.ts-login-link{display:inline-flex;align-items:center;gap:4px;background:var(--ts-card-bg) !important;border:none;padding:0;cursor:pointer;font-family:inherit;font-weight:600;font-size:12px;color:var(--ts-primary-light) !important;text-decoration:none;transition:opacity .2s,color .2s}
.ts-login-link svg{height:26px;width:auto;vertical-align:middle}
.ts-login-link:hover{opacity:.7;color:var(--ts-primary-dark) !important}
/* Dropzone */
.ts-dz{border:1.5px dashed var(--ts-dropzone-border);border-radius:var(--ts-btn-radius);background:var(--ts-surface);padding:14px 12px 8px;text-align:center;transition:border-color .15s,background .15s,box-shadow .15s;margin-top:0}
.ts-dz.ts-dz-preview:hover{border-color:var(--ts-primary-light);background:var(--ts-tint);box-shadow:0 4px 14px rgba(127,212,232,.12)}
.ts-dz.ts-dz-preview.over{border-color:var(--ts-primary-light);background:var(--ts-tint);box-shadow:0 4px 14px rgba(127,212,232,.16)}
.ts-up-copy{font-size:13px;color:${TEXT};line-height:1.5;margin:0 0 var(--ts-gap)}
.ts-up-copy strong{color:var(--ts-primary-dark);font-weight:700}
.ts-up-actions{display:flex;flex-direction:row;gap:var(--ts-gap)}
.ts-up-actions .ts-btn-primary,.ts-up-actions .ts-combo-cta{margin-top:0;flex:1;min-width:0;font-size:14px}
.ts-overlay .ts-up-actions .ts-btn-primary,.ts-overlay .ts-up-actions .ts-combo-cta{margin-top:0;flex:1;min-width:0;font-size:14px}
.ts-up-legal{font-size:10px;color:var(--ts-primary-light);margin-top:var(--ts-gap);line-height:1.4;letter-spacing:.01em;display:flex;align-items:center;justify-content:center;gap:3px;flex-wrap:wrap}
.ts-up-legal a{color:var(--ts-primary-light);text-decoration:underline;font-weight:600}
.ts-up-legal a:hover{color:var(--ts-primary-dark)}
/* Upload modal — uniform edge padding; dropzone flush so the gap = the modal pad */
.ts-card.ts-card-upload .ts-mbody{padding:var(--ts-modal-pad)}
.ts-card.ts-card-upload .ts-flow-panel{padding-bottom:0}
.ts-card.ts-card-upload .ts-topbar{display:none}
.ts-card.ts-card-upload .ts-dz:not(.ts-dz-preview){margin:0}
/* Preview state — same uniform edge padding around the image */
.ts-card.ts-card-preview{width:fit-content;max-width:440px}
.ts-card.ts-card-height{max-width:320px}
.ts-card.ts-card-glitch{width:fit-content;max-width:440px}
.ts-card.ts-card-preview .ts-mbody{padding:var(--ts-modal-pad)}
.ts-card.ts-card-preview .ts-flow-panel{padding-bottom:0}
.ts-card.ts-card-preview .ts-topbar{display:none}
.ts-up-preview{display:flex;flex-direction:column;align-items:center}
.ts-dz.ts-dz-preview{display:flex;justify-content:center;align-items:center;width:100%;padding:0;margin-top:0;border:none;border-radius:0;cursor:pointer;box-sizing:border-box}
.ts-dz.ts-dz-preview img{width:100%;height:auto;display:block;border-radius:var(--ts-btn-radius);opacity:0;transition:opacity .6s ease}
.ts-dz.ts-dz-preview img.ts-img-loaded{opacity:1}
/* Avatar preview — natural-ratio image (same hug as the upload preview and the
   processing scan), so the modal is the same size across preview → processing. */
.ts-dz.ts-avatar-preview{cursor:default}
.ts-dz.ts-avatar-preview img{width:100%;height:auto;max-height:none;object-fit:contain;opacity:0;transition:opacity .6s ease}
.ts-dz.ts-avatar-preview img.ts-img-loaded{opacity:1}
.ts-inp::placeholder{color:var(--ts-placeholder)}
/* Height dialog — small compact modal */
.ts-hoverlay{position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(15,23,42,.5);font-family:${FONT}}
.ts-hoverlay *{box-sizing:border-box}
.ts-hcard{background:var(--ts-card-bg);border-radius:var(--ts-btn-radius);width:100%;max-width:280px;padding:var(--ts-modal-pad);box-shadow:0 1px 2px rgba(15,23,42,.05),0 24px 64px -12px rgba(15,23,42,.24);position:relative;text-align:center;color:${TEXT}}
.ts-htitle{font-size:13px;font-weight:600;color:${TEXT};margin:0 0 var(--ts-gap);line-height:1.5}
.ts-hpick{position:relative;margin-bottom:var(--ts-gap)}
.ts-overlay .ts-hpicker{width:100%;padding:11px 12px;border:2px solid var(--ts-modal-primary);border-radius:var(--ts-btn-radius);background:var(--ts-card-bg);color:${TEXT};font-family:inherit;font-size:14px;font-weight:600;display:flex;align-items:center;justify-content:center;gap:8px;cursor:pointer;transition:border-color .15s,box-shadow .15s}
.ts-overlay .ts-hpicker:hover{border-color:var(--ts-primary-dark)}
.ts-overlay .ts-hpicker:focus{outline:none;border-color:var(--ts-primary-dark);box-shadow:0 0 0 3px rgba(127,212,232,.18)}
.ts-hchev{width:16px;height:16px;color:${MUTED};flex-shrink:0;transition:transform .2s ease}
.ts-hpicker.ts-hopen .ts-hchev{transform:rotate(180deg)}
.ts-hmenu{position:absolute;top:calc(100% + 6px);left:0;right:0;z-index:30;background:var(--ts-card-bg);border:1px solid ${BORDER};border-radius:var(--ts-btn-radius);box-shadow:0 12px 32px rgba(15,23,42,.18);overflow:hidden}
.ts-hmenu[hidden]{display:none}
.ts-hpage{display:none}
.ts-hpage.ts-hactive{display:block;animation:ts-hslide .18s ease}
@keyframes ts-hslide{from{opacity:0;transform:translateX(10px)}to{opacity:1;transform:none}}
.ts-hhead{display:flex;align-items:center;gap:6px;padding:8px 10px;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:${MUTED};border-bottom:1px solid ${BG}}
.ts-hback{width:24px;height:24px;border:none;border-radius:50%;background:${BG};color:${TEXT};font-size:16px;line-height:1;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;padding:0;transition:background .15s}
.ts-hback:hover{background:rgba(127,212,232,.22)}
.ts-hlist{max-height:180px;overflow-y:auto;padding:4px}
.ts-hlist::-webkit-scrollbar{width:6px}
.ts-hlist::-webkit-scrollbar-thumb{background:${BORDER};border-radius:3px}
.ts-hopt{width:100%;display:flex;justify-content:space-between;align-items:center;padding:9px 12px;border:none;border-radius:var(--ts-btn-radius);background:transparent;color:${TEXT};font-family:inherit;font-size:14px;cursor:pointer;text-align:left;transition:background .12s}
.ts-hopt:hover{background:${BG}}
.ts-hopt.ts-hopt-active{background:rgba(127,212,232,.16);color:var(--ts-primary-dark);font-weight:600}
.ts-hinp{text-align:center;border:1.5px dashed var(--ts-dropzone-border) !important;margin-bottom:var(--ts-gap)}
/* Inputs */
.ts-row{display:flex;gap:var(--ts-gap);margin-top:var(--ts-gap)}
.ts-row>div{flex:1}
.ts-inp{width:100%;padding:9px 11px;border:1px solid ${BORDER};border-radius:var(--ts-btn-radius);font-size:14px;outline:none;box-sizing:border-box;font-family:inherit;color:${TEXT};background:var(--ts-card-bg);transition:border-color .15s,box-shadow .15s}
.ts-inp:focus{outline:none;border-color:var(--ts-primary-light);box-shadow:0 0 0 3px rgba(127,212,232,.18)}
/* Buttons */
.ts-overlay .ts-btn-primary, .ts-hoverlay .ts-btn-primary{width:100%;padding:10px;border:none;border-radius:var(--ts-btn-radius);font-size:14px;font-weight:600;cursor:pointer;font-family:inherit;background:var(--ts-modal-primary) !important;background:linear-gradient(135deg,var(--ts-modal-primary),color-mix(in srgb,var(--ts-modal-primary) 55%,#1F6F8F)) !important;color:#fff !important;margin-top:10px;transition:transform .15s ease,box-shadow .15s ease,opacity .15s ease;box-shadow:0 4px 14px rgba(31,111,143,.28)}
.ts-overlay .ts-btn-primary:hover, .ts-hoverlay .ts-btn-primary:hover{opacity:.94;box-shadow:0 6px 18px rgba(31,111,143,.34);transform:translateY(-1px)}
.ts-overlay .ts-btn-primary:disabled, .ts-hoverlay .ts-btn-primary:disabled{opacity:.5;cursor:not-allowed;transform:none;box-shadow:none}
.ts-overlay .ts-btn-primary:focus-visible, .ts-hoverlay .ts-btn-primary:focus-visible, .ts-overlay .ts-btn-ghost:focus-visible{outline:none;box-shadow:0 0 0 3px rgba(127,212,232,.35)}
.ts-overlay .ts-btn-ghost{width:100%;padding:9px;background:var(--ts-card-bg) !important;border:1.5px solid var(--ts-modal-primary) !important;border-radius:var(--ts-btn-radius);font-size:13px;cursor:pointer;margin-top:8px;font-family:inherit;color:var(--ts-modal-primary) !important;box-shadow:0 1px 2px rgba(15,23,42,.05);transition:background .15s,border-color .15s,color .15s}
.ts-overlay .ts-btn-ghost:hover{background:rgba(127,212,232,.12);border-color:var(--ts-primary-dark);color:var(--ts-primary-dark)}
.ts-btn-secondary{width:100%;padding:12px;border:1px solid ${BORDER};border-radius:var(--ts-btn-radius);font-size:15px;font-weight:600;cursor:pointer;font-family:inherit;background:${BG};color:var(--ts-primary-dark);margin-top:10px;transition:background .15s,border-color .15s}
.ts-btn-secondary:hover{background:rgba(127,212,232,.12);border-color:rgba(127,212,232,.5)}
.ts-btn-secondary:disabled{opacity:.5;cursor:not-allowed}
/* Side-by-side primary + secondary action row (e.g. Try this … + Add Combo) */
.ts-btn-row{display:flex;gap:var(--ts-gap);margin-top:var(--ts-gap);width:100%}
.ts-btn-row .ts-btn-primary,.ts-btn-row .ts-combo-cta{flex:1;min-width:0;margin-top:0;padding:10px;font-size:14px;white-space:normal}
@media (max-width:360px){.ts-btn-row{flex-wrap:wrap}.ts-btn-row .ts-btn-primary,.ts-btn-row .ts-combo-cta{flex:1 1 100%}}
/* Add Combo — hollow button inheriting the modal button configurator
   (Modal Primary color + Modal Button radius) with a thick solid outline. */
.ts-combo-cta{
  display:inline-flex;align-items:center;justify-content:center;text-align:center;
  background:var(--ts-card-bg)!important;color:var(--ts-modal-primary)!important;
  border:3px solid var(--ts-modal-primary)!important;border-radius:var(--ts-btn-radius);
  font-family:inherit;font-weight:600;cursor:pointer;
  box-shadow:0 1px 2px rgba(15,23,42,.05);
  transition:background .15s,border-color .15s,color .15s,box-shadow .15s
}
.ts-combo-cta:hover:not(:disabled){background:rgba(127,212,232,.12)!important;border-color:var(--ts-primary-dark)!important;color:var(--ts-primary-dark)!important}
.ts-combo-cta:disabled{opacity:.5;cursor:not-allowed;box-shadow:none}
/* Error */
.ts-error{padding:10px 12px;background:var(--ts-error-bg);border:1px solid var(--ts-error-border);border-radius:var(--ts-btn-radius);color:${ERR};font-size:13px;margin-bottom:var(--ts-gap)}
/* ── Avatar grid ── */
.ts-avatar-hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--ts-gap)}
.ts-avatar-hdr h3{margin:0;font-size:16px;font-weight:700;color:${TEXT}}
.ts-avatar-upload-btn{background:var(--ts-modal-primary) !important;background:linear-gradient(135deg,var(--ts-modal-primary),color-mix(in srgb,var(--ts-modal-primary) 55%,#1F6F8F)) !important;color:#fff !important;border:none;border-radius:var(--ts-btn-radius);padding:var(--ts-btn-padding);font-size:var(--ts-btn-font);font-weight:600;cursor:pointer;font-family:inherit;box-shadow:0 2px 8px rgba(31,111,143,.2);white-space:nowrap}
.ts-avatar-upload-btn:hover{opacity:.92}
/* Avatar grid — device/orientation aware: portrait (mobile/phone) stays a narrow
   2-column card that scrolls tall; landscape / desktop gets a wide card so the
   grid spreads into more columns instead of stacking into a tall column. */
.ts-card.ts-card-avatars{max-width:440px}
@media (orientation:landscape) and (min-width:768px){
  .ts-card.ts-card-avatars{max-width:min(92vw, 860px)}
}
.ts-avatar-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:var(--ts-gap);margin-bottom:0}
.ts-avatar-thumb{position:relative;aspect-ratio:3/4;border-radius:var(--ts-btn-radius);overflow:hidden;cursor:pointer;border:2.5px solid transparent;transition:border-color .15s,transform .15s;background:${BG}}
.ts-avatar-thumb:hover{transform:translateY(-2px)}
.ts-avatar-thumb.selected{border-color:var(--ts-primary-light)}
.ts-avatar-thumb img{width:100%;height:100%;object-fit:cover;display:block}
.ts-avatar-check{position:absolute;top:8px;right:8px;width:22px;height:22px;background:var(--ts-primary-light);border-radius:50%;display:none;align-items:center;justify-content:center;color:#fff;font-size:13px;box-shadow:0 2px 6px rgba(15,23,42,.25)}
.ts-avatar-thumb.selected .ts-avatar-check{display:flex}
/* ── Processing sub-dialog ── */
.ts-proc-card{position:relative;background:var(--ts-card-bg);border-radius:var(--ts-btn-radius);padding:var(--ts-modal-pad);text-align:center;box-shadow:0 24px 64px -12px rgba(15,23,42,.24);max-width:300px;width:90%}
@keyframes ts-spin{to{transform:rotate(360deg)}}
.ts-spinner{width:56px;height:56px;border-radius:50%;border:3px solid ${BORDER};border-top-color:var(--ts-primary-light);animation:ts-spin .8s linear infinite;margin:0 auto 16px}
.ts-proc-title{font-weight:600;font-size:16px;color:${TEXT};margin:0 0 6px}
.ts-proc-sub{color:${MUTED};font-size:13px;margin:0}
.ts-proc-bar-wrap{width:100%;height:6px;background:${BORDER};border-radius:3px;overflow:hidden;margin:var(--ts-gap) 0 8px}
.ts-proc-bar{height:100%;background:linear-gradient(90deg,var(--ts-primary-light),var(--ts-primary-dark));border-radius:3px;transition:width .4s ease}
.ts-proc-pct{font-size:13px;font-weight:600;color:var(--ts-primary-light)}
/* ── Sci-Fi analytical scan (processing with a preview image) ──
   Frame holds the canvas sweep; the effect itself is drawn in glitch-sweep.ts. */
.ts-glitch{position:relative;width:100%;border-radius:var(--ts-btn-radius);overflow:hidden;background:#0B0F1A;line-height:0;border:1px solid #1A2636;box-shadow:0 0 30px rgba(0,210,255,.2)}
.ts-glitch .ts-scan-img{width:100%;height:auto;display:block;opacity:0;transition:opacity .3s ease}
.ts-glitch .ts-scan-img.ts-img-loaded{opacity:1}
.ts-glitch canvas{position:absolute;inset:0;width:100%;height:100%;display:block;border-radius:var(--ts-btn-radius)}
@media (prefers-reduced-motion: reduce){.ts-glitch{animation:none}}
/* ── Result ── */
/* ── Result Image & Floating Actions ── */
.ts-result-wrap {
  position: relative;
  margin: 0 0 var(--ts-gap);
  border-radius:var(--ts-btn-radius);
  overflow: hidden;
  background: var(--ts-result-bg);
  /* Ensure the video stays contained here */
  display: flex;
  flex-direction: column;
}

.ts-result-carousel{overflow:hidden}
.ts-result-track{display:flex;will-change:transform}
.ts-result-slide{flex:0 0 100%;min-width:0}
.ts-result-slide img{width:100%;height:auto;display:block;opacity:0;transition:opacity .6s ease}
.ts-result-slide img.ts-img-loaded{opacity:1}

.ts-result-actions {
  position: absolute;
  bottom: 12px;
  right: 12px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  z-index: 15; /* Higher than video to stay visible */
}

/* ── Split delivery: side/back view still generating ── */
.ts-mv-overlay{position:absolute;top:10px;left:50%;transform:translateX(-50%);display:flex;align-items:center;gap:6px;padding:7px 14px;border-radius:999px;background:rgba(15,23,42,.55);backdrop-filter:blur(4px);z-index:20;pointer-events:none}
.ts-mv-dot{width:7px;height:7px;border-radius:50%;background:#00d2ff;box-shadow:0 0 8px #00d2ff;animation:ts-breathe 1.8s ease-in-out infinite;flex:none}
.ts-mv-dot:nth-child(2){animation-delay:.3s}
.ts-mv-dot:nth-child(3){animation-delay:.6s}
.ts-mv-text{color:#fff;font-size:12px;white-space:nowrap;letter-spacing:.02em}
@keyframes ts-breathe{0%,100%{opacity:.25;transform:scale(.7)}50%{opacity:1;transform:scale(1)}}
.ts-mv-error{margin:var(--ts-gap) 0 0;text-align:center;color:${MUTED};font-size:12.5px}
@media (prefers-reduced-motion: reduce){.ts-mv-dot{animation:none;opacity:.85}}

/* Unified style for all circular floating buttons */
.ts-result-action, .ts-video-close {
  width: 38px !important;
  height: 38px !important;
  border-radius: 50% !important;
  background: rgba(15,23,42,.55) !important;
  border: none !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  color: #fff !important;
  transition: background .15s ease, transform .15s ease !important;
  padding: 0 !important;
  box-shadow: 0 2px 8px rgba(15,23,42,.25) !important;
}

.ts-result-action:hover, .ts-video-close:hover {
  background: rgba(15,23,42,.8) !important;
  transform: scale(1.05);
}

.ts-result-action svg, .ts-video-close svg {
  width: 18px !important;
  height: 18px !important;
}
/* Garment recommendation row — surfaced on the result card below "Try Again":
   medium-thick modal-primary border, no padding (inner rows keep their own
   spacing), global radius; margins use the modal pad + element gap tokens. */
.ts-size-card{border:3px solid var(--ts-modal-primary);border-radius:var(--ts-btn-radius);padding:0;margin:var(--ts-gap) 0 0;background:var(--ts-card-bg)}
.ts-size-card .ts-size-title,.ts-size-card .ts-size-row{padding-left:14px;padding-right:14px}
.ts-size-title{font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:${TEXT};margin:0 0 var(--ts-gap);display:flex;align-items:center;gap:7px;padding-top:12px}
.ts-size-title svg{flex-shrink:0}
.ts-size-row{display:flex;justify-content:space-between;align-items:center;font-size:14px;padding:5px 0;border-top:1px solid ${BORDER}}
.ts-size-row:first-of-type{border-top:none}
.ts-size-label{color:${MUTED};font-size:13.5px}
.ts-size-value{font-weight:700;color:var(--ts-primary-dark);font-size:14px}
/* App store icons — compact */
.ts-store-row{display:flex;align-items:center;justify-content:space-between;gap:var(--ts-gap);margin:var(--ts-gap) 0 0;flex-wrap:wrap}
.ts-store-text{font-size:11px;color:${MUTED};line-height:1.4;flex:1;min-width:0}
.ts-store-icons{display:flex;align-items:center;gap:var(--ts-gap)}
.ts-store-icons a,.ts-store-logout{display:inline-flex;align-items:center;justify-content:center;opacity:.92;transition:opacity .15s,transform .15s;cursor:pointer}
.ts-store-icons a:hover,.ts-store-logout:hover{opacity:1;transform:translateY(-1px)}
.ts-store-icons a img{height:30px;border-radius:5px}
.ts-store-logout{width:30px;height:30px;padding:0;background:none;border:none;color:var(--ts-modal-primary)}
.ts-store-logout:hover{color:var(--ts-primary-dark)}
/* ── Login phase (rendered inside the shared card) — every element follows the
   configurator vars (no hardcoded sizes/colors). ── */
.ts-card-login{max-width:360px}
.ts-login-hdr{text-align:center;margin-bottom:var(--ts-gap)}
.ts-powered-row{display:flex;align-items:center;justify-content:center;gap:6px;margin-bottom:6px}
.ts-powered-by{font-size:13px;color:${MUTED}}
.ts-login-title{margin:0;font-size:15px;font-weight:400;font-family:inherit;color:${TEXT};display:flex;align-items:center;justify-content:center;gap:8px}
.ts-login-logo{display:inline-flex;align-items:center;line-height:1}
.ts-login-social{display:flex;gap:var(--ts-gap);margin-bottom:var(--ts-gap)}
.ts-card-login .ts-social-btn{flex:1;min-height:46px;display:flex;align-items:center;justify-content:center;gap:var(--ts-gap);padding:0;background:var(--ts-card-bg) !important;border:1.5px solid var(--ts-modal-primary) !important;border-radius:var(--ts-btn-radius);cursor:pointer;transition:border-color .2s,background .2s;margin-bottom:0;color:var(--ts-text)}
.ts-card-login .ts-social-btn svg{display:block}
.ts-card-login .ts-social-btn:hover{border-color:var(--ts-primary-dark) !important;background:rgba(127,212,232,.12) !important}
.ts-card-login .ts-btn-primary{margin-top:0;padding:var(--ts-btn-padding);font-size:var(--ts-btn-font)}
.ts-card-login .ts-inp{padding:var(--ts-btn-padding);font-size:var(--ts-btn-font);margin-bottom:var(--ts-gap);border:1.5px solid var(--ts-modal-primary);border-radius:var(--ts-btn-radius);background:var(--ts-card-bg)}
.ts-card-login .ts-inp:focus{border-color:var(--ts-primary-dark);box-shadow:0 0 0 3px rgba(127,212,232,.18)}
.ts-or-divider{display:flex;align-items:center;gap:var(--ts-gap);margin:var(--ts-gap) 0;color:${MUTED};font-size:13px}
.ts-or-line{flex:1;height:1px;background:${BORDER}}
/* Trigger button */
.ts-overlay .ts-tryon-btn{display:inline-flex;width:auto;white-space:nowrap;align-items:center;justify-content:center;background:var(--ts-modal-primary) !important;background:linear-gradient(135deg,var(--ts-modal-primary),color-mix(in srgb,var(--ts-modal-primary) 55%,#1F6F8F)) !important;padding:var(--ts-btn-padding);font-weight:600;font-size:var(--ts-btn-font);text-align:center;border-radius:var(--ts-btn-radius);color:#fff !important;border:none;cursor:pointer;box-sizing:border-box;letter-spacing:.01em;font-family:${FONT};box-shadow:0 4px 14px rgba(31,111,143,.28) !important;transition:transform .15s ease,box-shadow .15s ease,opacity .15s ease}
.ts-overlay .ts-tryon-btn:hover{opacity:.94;box-shadow:0 6px 18px rgba(31,111,143,.34) !important;transform:translateY(-1px)}
.ts-overlay .ts-tryon-btn.ts-disabled{opacity:.6;cursor:not-allowed;background:#999 !important;box-shadow:none !important;transform:none}
.ts-powered{display:flex;align-items:center;justify-content:center;gap:5px;margin-top:7px;font-size:12px;color:var(--ts-powered);font-family:${FONT};user-select:none}
.ts-init-error{text-align:center;font-size:12px;color:${ERR};margin-top:6px;font-family:${FONT}}
/* ── Multiview carousel ── */
.ts-mv-header{display:flex;align-items:center;gap:var(--ts-gap);margin-bottom:var(--ts-gap)}
.ts-mv-badge{background:var(--ts-primary-light);color:#fff;font-size:10px;font-weight:700;letter-spacing:.06em;padding:2px 8px;border-radius:99px;text-transform:uppercase}
.ts-mv-counter{font-size:13px;font-weight:600;color:${TEXT};flex:1}
.ts-mv-page{font-size:12px;color:${MUTED};white-space:nowrap}
.ts-mv-arrow{position:absolute;top:50%;transform:translateY(-50%);width:36px;height:36px;border-radius:50%;background:rgba(255,255,255,.94);border:1px solid ${BORDER};box-shadow:0 2px 10px rgba(15,23,42,.14);cursor:pointer;display:flex;align-items:center;justify-content:center;color:#444;transition:background .15s,box-shadow .15s;z-index:2;padding:0}
.ts-mv-arrow:hover{background:#fff;box-shadow:0 4px 14px rgba(15,23,42,.2)}
.ts-mv-prev{left:10px}
.ts-mv-next{right:10px}
.ts-mv-pills{display:flex;gap:var(--ts-gap);margin:var(--ts-gap) 0 0}
.ts-mv-pill{flex:1;padding:8px 0;font-size:12px;font-weight:500;border-radius:var(--ts-btn-radius);border:1px solid ${BORDER};background:var(--ts-card-bg) !important;color:${TEXT} !important;cursor:pointer;font-family:inherit;transition:all .15s;white-space:nowrap}
.ts-mv-pill.active{background:var(--ts-primary-dark) !important;border-color:var(--ts-primary-light);color:#fff !important;font-weight:600}
/* ── Fullscreen video player ── */
.ts-video-fullscreen {
  position: absolute;
  /* This ensures it only covers the image/result area, not the whole card */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: #000;
  border-radius:var(--ts-btn-radius);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ts-video-fullscreen video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  flex: 1;
}

/* Specific position for video close button within the result wrap */
.ts-video-close {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 12;
}

.ts-video-toolbar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px;
  background: linear-gradient(transparent, rgba(0,0,0,0.7));
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  z-index: 11;
}

.ts-video-toolbar button {
  padding: 6px 14px;
  border-radius:var(--ts-btn-radius);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  background: var(--ts-primary-light);
  color: #fff;
  border: none;
}

/* Fullscreen result image lightbox (viewport height, same page) */
.ts-result-lightbox {
  position: fixed;
  inset: 0;
  z-index: 100001;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: env(safe-area-inset-top, 0) env(safe-area-inset-right, 0) env(safe-area-inset-bottom, 0) env(safe-area-inset-left, 0);
  box-sizing: border-box;
  font-family: ${FONT};
}
.ts-result-lightbox button {
  font: inherit;
  color: inherit;
  background: transparent;
  border: 0;
  outline: none;
  margin: 0;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  box-sizing: border-box;
}
.ts-result-lightbox-inner {
  position: relative;
  width: 100%;
  height: 100%;
  max-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.ts-result-lightbox-inner img {
  pointer-events: auto;
  max-width: 100vw;
  width: auto;
  height: 100vh;
  max-height: 100vh;
  object-fit: contain;
}
.ts-result-lightbox-close {
  position: fixed;
  top: max(12px, env(safe-area-inset-top, 12px));
  right: max(12px, env(safe-area-inset-right, 12px));
  z-index: 100002;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
}
.ts-result-lightbox-close:hover {
  background: rgba(255, 255, 255, 0.15);
}
.ts-result-lightbox-arrow {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  z-index: 100002;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid ${BORDER};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
  padding: 0;
}
.ts-result-lightbox-arrow:hover {
  background: #fff;
}
.ts-result-lightbox-prev {
  left: max(12px, env(safe-area-inset-left, 12px));
}
.ts-result-lightbox-next {
  right: max(12px, env(safe-area-inset-right, 12px));
}
/* ── Combo carousel ── */
/* The card width is pinned inline when combo opens; only the height grows. */
.ts-combo-carousel{width:100%;margin-top:var(--ts-gap);animation:ts-combo-in .35s ease}
@keyframes ts-combo-in{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
.ts-combo-section-title{flex:0 0 auto;align-self:center;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:${MUTED};background:${BG};border:1px solid ${BORDER};padding:3px 10px;border-radius:99px;margin:0 4px 0 8px;white-space:nowrap}
.ts-combo-track{display:flex;align-items:flex-start;gap:var(--ts-gap);overflow-x:auto;padding:2px 2px 8px;scroll-snap-type:x proximity;-webkit-overflow-scrolling:touch}
.ts-combo-track::-webkit-scrollbar{height:6px}
.ts-combo-track::-webkit-scrollbar-thumb{background:${BORDER};border-radius:3px}
.ts-combo-item{flex:0 0 132px;scroll-snap-align:start;display:flex;flex-direction:column;align-items:stretch;background:var(--ts-card-bg)!important;border:1.5px solid ${BORDER};border-radius:var(--ts-btn-radius);padding:6px;cursor:pointer;font-family:inherit;color:${TEXT};text-align:left;transition:border-color .15s,box-shadow .15s,transform .15s}
.ts-combo-item:hover{border-color:var(--ts-primary-light);box-shadow:0 4px 14px rgba(127,212,232,.14);transform:translateY(-1px)}
.ts-combo-item.selected{border-color:var(--ts-primary-dark);box-shadow:0 0 0 3px rgba(127,212,232,.25)}
.ts-combo-thumb{position:relative;aspect-ratio:3/4;border-radius:var(--ts-btn-radius);overflow:hidden;background:${BG};display:flex;align-items:center;justify-content:center;margin-bottom:6px}
.ts-combo-thumb img{width:100%;height:100%;object-fit:cover;display:block}
.ts-combo-noimg{color:${MUTED};font-size:18px}
.ts-combo-check{position:absolute;top:6px;right:6px;width:22px;height:22px;border-radius:50%;background:var(--ts-primary-dark);display:none;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(15,23,42,.3)}
.ts-combo-item.selected .ts-combo-check{display:flex}
.ts-combo-name{font-size:12px;font-weight:600;color:${TEXT};line-height:1.35;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.ts-combo-loading,.ts-combo-error{padding:16px 12px;text-align:center;margin-top:var(--ts-gap);border:1.5px dashed ${BORDER};border-radius:var(--ts-btn-radius);background:var(--ts-surface)}
.ts-combo-loading p{margin:0;font-size:13px;color:${MUTED}}
.ts-combo-error{font-size:13px;color:${ERR}}
`;
  document.head.appendChild(s);
}
